import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Divider,
  Button,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAuth } from "../../contexts/AuthContext";
import { getMySubscription, getPlans } from "../../services/clientService";
import { getInvoicesList } from "../../services/invoiceService";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api from "../../utils/api";

const formatCurrency = (value) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(value || 0));
const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
};

const InvoiceDocument = ({
  brandName,
  brandAddress,
  invoiceTitle,
  invoiceId,
  invoiceDate,
  statusLabel,
  billToName,
  billToEmail,
  items,
  subtotal,
  taxLabel,
  total,
  note,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2.5, sm: 3.5 },
      borderRadius: 2,
      border: "1px solid #e5e7eb",
      backgroundColor: "#ffffff",
      maxWidth: 820,
      mx: "auto",
    }}
  >
    <Box
      sx={{
        height: 6,
        borderRadius: 999,
        background: "linear-gradient(90deg, #0f172a 0%, #2563eb 100%)",
      }}
    />
    <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2} mt={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
          {brandName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {brandAddress}
        </Typography>
      </Box>
      <Box textAlign="right">
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {invoiceTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Invoice ID: {invoiceId}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Date: {invoiceDate}
        </Typography>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            mt: 1,
            fontWeight: 600,
            backgroundColor: statusLabel === "Paid" ? "#dcfce7" : "#fee2e2",
            color: statusLabel === "Paid" ? "#166534" : "#991b1b",
          }}
        />
      </Box>
    </Box>

    <Divider sx={{ my: 3 }} />

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Billed To
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {billToName}
        </Typography>
        {billToEmail && (
          <Typography variant="body2" color="text.secondary">
            {billToEmail}
          </Typography>
        )}
      </Box>
      <Box textAlign={{ xs: "left", md: "right" }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Summary
        </Typography>
        <Typography variant="body2">Items: {items.length}</Typography>
        <Typography variant="body2">Subtotal: {formatCurrency(subtotal)}</Typography>
      </Box>
    </Box>

    <Table size="small" sx={{ mt: 3 }}>
      <TableHead>
        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
          <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="right">
            Qty
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="right">
            Price
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="right">
            Amount
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item, index) => (
          <TableRow key={`${item.description}-${index}`}>
            <TableCell>{item.description}</TableCell>
            <TableCell>{item.period}</TableCell>
            <TableCell align="right">{item.qty}</TableCell>
            <TableCell align="right">{formatCurrency(item.price)}</TableCell>
            <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>

    <Divider sx={{ my: 3 }} />

    <Box display="flex" justifyContent="flex-end">
      <Box textAlign="right">
        <Typography variant="body2" color="text.secondary">
          Subtotal: {formatCurrency(subtotal)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {taxLabel}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
          Total: {formatCurrency(total)}
        </Typography>
      </Box>
    </Box>

    {note && (
      <>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {note}
        </Typography>
      </>
    )}
  </Paper>
);

export default function ClientInvoicesPage() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [subscriptionData, invoiceData] = await Promise.all([
          (async () => {
            try {
              const data = await getMySubscription();
              const sub = data?.subscription ?? (data?.plan ? data : null);
              if (!sub) return null;
              try {
                const plans = await getPlans();
                const list = Array.isArray(plans) ? plans : Array.isArray(plans?.results) ? plans.results : [];
                const match = list.find(
                  (p) => String(p.name || "").toLowerCase() === String(sub.plan || "").toLowerCase()
                );
                const price = match ? parseFloat(match.price) : undefined;
                return { ...sub, price };
              } catch (error) {
                return sub;
              }
            } catch (error) {
              return null;
            }
          })(),
          (async () => {
            try {
              const list = await getInvoicesList();
              return Array.isArray(list) ? list : [];
            } catch (error) {
              console.error("Failed to load invoices", error);
              setSnackbar({ open: true, message: "Failed to load invoices." });
              return [];
            }
          })(),
        ]);

        setSubscription(subscriptionData);
        setInvoices(invoiceData);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const downloadInvoicePreview = async (elementId, filename) => {
    const invoiceElement = document.getElementById(elementId);
    if (!invoiceElement) return;
    try {
      const canvas = await html2canvas(invoiceElement, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${filename}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setSnackbar({ open: true, message: "Failed to generate PDF." });
    }
  };

  const handlePayment = async (invoice) => {
    try {
      setPayingId(invoice.invoice_id);
      const total = Number(invoice.total_amount || 0);
      if (!Number.isFinite(total) || total <= 0) {
        setSnackbar({ open: true, message: "Invalid invoice total." });
        return;
      }
      const response = await api.post("/create-checkout-session/", {
        amount: Math.round(total * 100),
        currency: "gbp",
      });
      if (response?.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Payment init failed:", error);
      setSnackbar({ open: true, message: "Failed to start payment. Try again." });
    } finally {
      setPayingId(null);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Container sx={{ mt: 5 }}>
      <Typography variant="h4" gutterBottom>My Invoices</Typography>

      {subscription ? (
        <Accordion disableGutters sx={{ borderRadius: 2, boxShadow: 3, mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <Typography variant="h6">Subscription Invoice  {new Date().toLocaleDateString()}</Typography>
              <Typography variant="body2">Total: {formatCurrency(subscription?.price)}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box id="subscription-invoice">
              <InvoiceDocument
                brandName="Delight Supported Living"
                brandAddress="63 Knowl Piece, Wilbury Way, Hitchin, SG4 0TY, United Kingdom"
                invoiceTitle="Invoice"
                invoiceId={`SUB-${user?.id || "0001"}`}
                invoiceDate={new Date().toLocaleDateString()}
                statusLabel="Paid"
                billToName={`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.username || "Client"}
                billToEmail={user?.email || ""}
                items={[
                  {
                    description: `${subscription.plan} Plan`,
                    period: `${formatDate(subscription.start_date)} - ${formatDate(subscription.expiry_date)}`,
                    qty: 1,
                    price: Number(subscription?.price || 0),
                    amount: Number(subscription?.price || 0),
                  },
                ]}
                subtotal={Number(subscription?.price || 0)}
                taxLabel="VAT (0%): GBP 0.00"
                total={Number(subscription?.price || 0)}
                note="Thank you for choosing Delight Supported Living."
              />
            </Box>
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="contained" onClick={() => downloadInvoicePreview("subscription-invoice", `Invoice_${user?.username || "client"}`)}>
                Download Invoice (PDF)
              </Button>
              <Button variant="outlined" onClick={() => handlePayment({ invoice_id: -1, total_amount: subscription?.price || 0 })} disabled={payingId === -1}>
                {payingId === -1 ? "Redirecting" : `Make Payment (${formatCurrency(subscription?.price)})`}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      ) : (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No subscription invoice available.
        </Typography>
      )}

      {invoices.length > 0 && (
        <Box mt={3}>
          <Typography variant="h5" gutterBottom>Additional Invoices</Typography>
          {invoices.map((inv) => (
            <Accordion key={inv.invoice_id} disableGutters sx={{ borderRadius: 2, boxShadow: 3, mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Typography variant="h6">Invoice  {formatDate(inv.invoice_datetime)}</Typography>
                  <Typography variant="body2">Total: {formatCurrency(inv.total_amount)}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box id={`client-invoice-${inv.invoice_id}`}>
                  <InvoiceDocument
                    brandName="Delight Supported Living"
                    brandAddress="63 Knowl Piece, Wilbury Way, Hitchin, SG4 0TY, United Kingdom"
                    invoiceTitle="Invoice"
                    invoiceId={`INV-${inv.invoice_id}`}
                    invoiceDate={formatDate(inv.invoice_datetime)}
                    statusLabel={inv.is_paid ? "Paid" : "Unpaid"}
                    billToName={`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.username || "Client"}
                    billToEmail={user?.email || ""}
                    items={[
                      {
                        description: inv.service_description || inv.details?.description || "Invoice",
                        period: formatDate(inv.invoice_datetime),
                        qty: 1,
                        price: Number(inv.total_amount || 0),
                        amount: Number(inv.total_amount || 0),
                      },
                    ]}
                    subtotal={Number(inv.total_amount || 0)}
                    taxLabel="VAT (0%): GBP 0.00"
                    total={Number(inv.total_amount || 0)}
                    note="If you have any questions about this invoice, contact our support team."
                  />
                </Box>
                <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    onClick={() =>
                      downloadInvoicePreview(
                        `client-invoice-${inv.invoice_id}`,
                        `Invoice_${inv.invoice_id}`
                      )
                    }
                  >
                    Download Invoice (PDF)
                  </Button>
                  {!inv.is_paid && (
                    <Button
                      variant="outlined"
                      disabled={payingId === inv.invoice_id}
                      onClick={() => handlePayment(inv)}
                    >
                      {payingId === inv.invoice_id ? "Redirecting" : `Make Payment (${formatCurrency(inv.total_amount)})`}
                    </Button>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
}
